import type { FlightMeta, HistoryPoint } from './types';

/**
 * Where the PlaneSight server lives.
 *
 * Unset (the default), the client talks to whoever served the page: the
 * Fastify server in Docker/Tailscale, or Vite's dev proxy. When the static
 * site is hosted separately (Vercel), set VITE_API_BASE at build time to the
 * server's public origin, e.g. https://planesight.tailnet-name.ts.net, and
 * add the site's origin to the server's CORS_ORIGINS.
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '');

/** Absolute or same-origin URL for an API path like `/api/tle`. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  const res = await fetch(apiUrl(path), { signal });
  return res.ok ? ((await res.json()) as T) : null;
}

/** The aircraft's current (or most recent) flight with its whole recorded track. */
export function fetchCurrentTrack(icao24: string, signal?: AbortSignal) {
  return getJson<{ flight: FlightMeta; track: HistoryPoint[] }>(`/api/aircraft/${icao24}/track`, signal);
}

/** Recorded flights for one airframe, newest first (server keeps 30 days). */
export function fetchAircraftFlights(icao24: string, signal?: AbortSignal, limit = 30) {
  return getJson<{ flights: FlightMeta[] }>(`/api/aircraft/${icao24}/flights?limit=${limit}`, signal);
}

export function fetchFlight(id: number, signal?: AbortSignal) {
  return getJson<{ flight: FlightMeta; track: HistoryPoint[] }>(`/api/flights/${id}`, signal);
}

/** WebSocket URL for the live traffic stream. */
export function wsUrl(): string {
  if (API_BASE) return `${API_BASE.replace(/^http/, 'ws')}/ws`;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws`;
}
